'use client';

// app/free-pentest/pentest-client.tsx - free AI pentest report request form
// No captcha by design: spam is handled by honeypot + min-fill-time + rate limit + domain validation.
import { useRef, useState } from 'react';
import Link from 'next/link';

const DOMAIN_RE = /^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

function parseDomains(raw: string): { valid: string[]; invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const part of raw.split(/[\s,;]+/)) {
    const d = part.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/:\d+$/, '');
    if (!d) continue;
    if (DOMAIN_RE.test(d)) {
      if (!valid.includes(d)) valid.push(d);
    } else {
      invalid.push(d);
    }
  }
  return { valid, invalid };
}

const inputCls =
  'w-full bg-surface-base border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white/80 placeholder:text-zinc-700 focus:outline-none focus:border-accent/30 transition-colors';

export default function PentestRequestClient({ source }: { source: string }) {
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [msg, setMsg] = useState('');
  const startedAt = useRef<number>(Date.now());

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setMsg('');

    const { valid, invalid } = parseDomains(String(form.get('domains') || ''));
    if (invalid.length) {
      setMsg(`These do not look like valid domains: ${invalid.slice(0, 3).join(', ')}${invalid.length > 3 ? '…' : ''}`);
      return;
    }
    if (valid.length === 0) {
      setMsg('Enter at least one domain, for example acme.com');
      return;
    }
    if (valid.length > 10) {
      setMsg('Maximum 10 domains per request. List your most important ones first.');
      return;
    }
    if (!form.get('authorized')) {
      setMsg('Please confirm you are authorized to request testing for these domains.');
      return;
    }

    setState('sending');
    try {
      const payload = {
        name: form.get('name'),
        email: form.get('email'),
        company: form.get('company'),
        domains: valid,
        notes: form.get('notes'),
        source: source || 'free_pentest_page',
        form_ms: Date.now() - startedAt.current,
        company_url_confirm: form.get('company_url_confirm'),
      };
      const res = await fetch('/api/pentest-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await res.json().catch(() => ({} as any));
      if (res.ok && j?.ok !== false) {
        setState('sent');
      } else {
        setState('error');
        setMsg(j?.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setState('error');
      setMsg('Network error. Please try again.');
    }
  }

  return (
    <section className="relative py-16 min-h-screen bg-surface-base">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(0,201,160,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,201,160,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="relative max-w-2xl mx-auto px-6">
        {/* Header */}
        <div className="mb-12 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent/20 bg-accent/[0.06] text-accent text-xs font-medium mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            Cyber Swarm · AI Penetration Testing
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white/90 tracking-tight mb-4">
            Request a Free<br /><span className="text-accent">AI Pentest Report</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-md mx-auto">
            Hundreds of AI agents actively test your external attack surface. Every finding comes with real exploitation evidence.
          </p>
        </div>

        {state === 'sent' ? (
          <div className="rounded-2xl border border-accent/20 bg-surface-1 p-12 flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center mb-5">
              <svg className="w-7 h-7 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-2xl font-bold text-white/90 mb-3">Request received.</h2>
            <p className="text-sm text-zinc-400 max-w-sm leading-relaxed mb-8">
              We will reach out within 1 business day to verify domain ownership via a DNS TXT record. Once verified, the agents run and your report lands in your inbox.
            </p>
            <Link href="/cyberswarm" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/10 text-zinc-300 hover:text-white hover:border-white/20 transition-all text-sm">
              Learn more about Cyber Swarm →
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="rounded-2xl border border-white/[0.06] bg-surface-1 p-7 sm:p-9 space-y-5">
            {/* Honeypot */}
            <input type="text" name="company_url_confirm" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-500 mb-1.5 block">Full name *</label>
                <input name="name" required placeholder="Jane Doe" className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1.5 block">Work email *</label>
                <input name="email" type="email" required placeholder="you@company.com" className={inputCls} />
              </div>
            </div>

            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Company</label>
              <input name="company" placeholder="Acme Corp" className={inputCls} />
            </div>

            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Domains to test *</label>
              <textarea name="domains" required rows={3} placeholder={'acme.com\napp.acme.com'}
                className={`${inputCls} resize-y font-mono`} />
              <p className="text-[11px] text-zinc-600 mt-1.5">One per line or comma-separated. Up to 10 domains. We only test domains you verify via DNS TXT record.</p>
            </div>

            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Anything we should know?</label>
              <textarea name="notes" rows={2} placeholder="Compliance deadline, scope notes, current testing cadence..." className={`${inputCls} resize-y`} />
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" name="authorized" className="mt-0.5 accent-[#00C9A0]" />
              <span className="text-xs text-zinc-400 leading-relaxed">
                I own these domains or am authorized to request security testing for them. Testing only begins after DNS TXT verification.
              </span>
            </label>

            {msg && <p className={`text-sm ${state === 'error' ? 'text-red-400' : 'text-yellow-400'}`}>{msg}</p>}

            <button type="submit" disabled={state === 'sending'}
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-accent text-black font-bold hover:bg-accent-hover transition-all duration-300 shadow-lg shadow-accent/20 hover:shadow-accent/35 disabled:opacity-50 disabled:cursor-not-allowed">
              {state === 'sending' ? 'Sending…' : 'Get My Free AI Pentest Report'}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </button>
            <p className="text-[11px] text-zinc-600 text-center">
              No commitment. We respond within 1 business day. By submitting you agree to our{' '}
              <Link href="/terms" className="underline hover:text-zinc-400 transition-colors">Terms of Service</Link>, including the testing authorization and assumption-of-risk provisions.
            </p>
          </form>
        )}

        {/* What happens next */}
        {state !== 'sent' && (
          <div className="mt-10 grid sm:grid-cols-3 gap-3">
            {[
              ['1 · Submit', 'Tell us which domains to test.'],
              ['2 · Verify', 'Prove ownership with a DNS TXT record, like Google Search Console.'],
              ['3 · Report', 'AI agents run the pentest. Auditor-ready report in your inbox.'],
            ].map(([step, desc]) => (
              <div key={step} className="rounded-xl border border-white/[0.06] bg-surface-1 p-5">
                <p className="text-xs font-mono text-accent mb-1.5">{step}</p>
                <p className="text-xs text-zinc-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
